import { useState, useRef, useCallback } from "react";
import { ScreenNameList } from "@lib/utilities/Constants";
import { useAppDispatch, useAppSelector } from "@lib";
import { useFocusEffect } from "@react-navigation/native";
import { DocumentItem } from "./DocumentParser";
import Crashlytics from "@lib/utilities/crashlyticsUtils/CrashlyticsUtils";
import { DOCUMENTS_INFO_ROUTE } from "../../../src/navigation/NavigationConstants";
import { messages } from "../../../src/resources/strings";
import {
  resetPolicyDetail,
  setError,
  setLoading,
} from "@lib";
import { documentHandler } from "@lib";

export const useDocumentsInfo = (navigation: any) => {
  const flatListRef = useRef();
  // States
  const [itemPressedFlag, setItemPressedFlag] = useState(false);

  const [documentsArray, setDocumentsArray] = useState<DocumentItem[]>([]);

  const [maxVisibleIndex, setMaxVisibleIndex] = useState(0);
  const [minVisibleIndex, setMinVisibleIndex] = useState(0);
  const [pdfNavigationFlag, setPdfNavigationFlag] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // redux
  const policyData = useAppSelector((state) => state.policyReducer);
  const dispatch = useAppDispatch();

  const hasFetchedDocsRef = useRef(false);
  const {
    coverageDetails,
    loading,
    error,
    policyNumber: detailedPolicy,
  } = useAppSelector((state) => state.policyDetailReducer);

  const { fetchDocs, callIntrospectionAndGetPdfApi, callGetDocumentUrlsAPI } =
    documentHandler({
      setItemPressedFlag,
      setPdfNavigationFlag,
      setDocumentsArray,
      navigation,
    });
  const errorData = {
    message: error,
    screenName: ScreenNameList.documentInfo,
  };

  // initialise
  useFocusEffect(
    useCallback(() => {
      Crashlytics.logScreen(DOCUMENTS_INFO_ROUTE);

      if (!pdfNavigationFlag && policyData?.policies?.length) {
        hasFetchedDocsRef.current = true;
        fetchDocs()
          .then(() => {
            hasFetchedDocsRef.current = false;
          })
          .catch(() => {
            hasFetchedDocsRef.current = false;
          });
      }
      setItemPressedFlag(false);
    }, [
      policyData?.policies?.length,
      pdfNavigationFlag,
      itemPressedFlag,
      policyData.policyNumber,
    ])
  );

  // Event Handlers
  const clearStateData = () => {
    dispatch(resetPolicyDetail());
  };

  const settingsBeforeCallingApi = async (policyNumber: string) => {
    dispatch(setError(messages.error.noError));
    clearStateData();
    callGetDocumentUrlsAPI(policyNumber);
  };
  const onReloadPress = async () => {
    settingsBeforeCallingApi(policyData.policyNumber);
  };

  // FlatList Handlers
  const onViewableItemsChanged = useCallback(({ viewableItems }: any) => {
    if (viewableItems.length > 0) {
      setMaxVisibleIndex(viewableItems[viewableItems.length - 1].index);
      setMinVisibleIndex(viewableItems[0].index);
    }
  }, []);
  const moveNextPreviousHorizontalFlatlist = (isNext: boolean) => {
    if (flatListRef.current != null) {
      if (isNext) {
        if (
          documentsArray.length > 0 &&
          currentIndex < documentsArray.length - 2
        ) {
          const nextIndex = currentIndex + 1;
          if (nextIndex < documentsArray.length) {
            setCurrentIndex(nextIndex);
            // @ts-ignore
            flatListRef.current.scrollToIndex({
              index: nextIndex,
              animated: true,
            });
          }
        }
      } else {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          setCurrentIndex(prevIndex);
          // @ts-ignore
          flatListRef.current.scrollToIndex({
            index: prevIndex,
            animated: true,
          });
        }
      }
    }
  };
  const onEndTimer = () => {
    dispatch(setError(messages.error.noError));
  };

  return {
    // States
    arrayPolicyList: policyData.policies,
    coverageDetails,
    detailedPolicy,

    documentsArray,
    loading: loading,

    maxVisibleIndex,
    minVisibleIndex,
    flatListRef,

    currentIndex,
    itemPressedFlag,
    setItemPressedFlag,
    errorData,
    onEndTimer,

    // Functions
    onReloadPress,
    moveNextPreviousHorizontalFlatlist,
    onViewableItemsChanged,
    onPdfItemTap: (item: DocumentItem) => {
      callIntrospectionAndGetPdfApi(item);
    },
    setErrorHandle: (message: string) => dispatch(setError(message)),
  };
};
